/**
 * KpiPanel (P280.3) -- effectiveness KPIs for a control + Test of
 * Effectiveness derivation. Auto KPIs are fed from the twin (drill
 * pass-rate, finding MTTR, attestation timeliness, availability); manual
 * KPIs take operator readings. ToE derives deterministically from the
 * latest readings vs tolerance -- effectiveness measured, not asserted.
 */
import { formatTime } from "shared/time/formatDate";
import { formatDateTime } from "shared/time/formatDate";
import { i18n } from "shared/i18n";
import { InfoTip } from "shared/components/Explainer";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  useAddKpiReading,
  useControlKpis,
  useCreateKpi,
  useDeleteKpi,
  useDeriveToE,
  useKpiReadings,
  useSampleKpis,
} from "framework/api/kpi_hooks";
import { Sparkline } from "shared/components/Sparkline";
import { StatusPill } from "shared/components/StatusPill";
import { useToast } from "shared/toast/ToastProvider";
import { useMe } from "shared/api/useMe";
import type { ControlKpi, ControlKpiCreateIn } from "shared/api/types";

interface Props {
  cycleId: string;
  controlId: string;
}

export function KpiPanel({ cycleId, controlId }: Props): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const kpis = useControlKpis(controlId);
  const deriveToE = useDeriveToE(cycleId, controlId);
  const sample = useSampleKpis(controlId);
  const isAdmin = useMe().data?.is_admin ?? false;

  const runDerive = async () => {
    try {
      const res = await deriveToE.mutateAsync();
      toast.push({
        tone: "success",
        message: t("modules.framework.kpi.toeDerived", {
          result: t(`modules.framework.controlTesting.result.${res.test.result}`),
        }),
      });
    } catch (e) {
      const err = e as { status?: number };
      toast.push({
        tone: "warning",
        message:
          err.status === 422
            ? t("modules.framework.kpi.noKpis")
            : t("modules.framework.kpi.deriveError"),
      });
    }
  };

  const list = kpis.data?.kpis ?? [];
  /* Round 11 D5. The panel offers a sampling verb only when there is
     something waiting on it -- an auto KPI with no reading and
     sampling switched off. A button that is always there, on a panel
     where nothing needs it, is one more control to read past. */
  const waitingOnSampling =
    kpis.data?.sampling_enabled === false &&
    list.some((k) => k.source === "auto" && !k.latest_reading);
  const runSample = () => {
    sample.mutate(undefined, {
      onSuccess: (r) =>
        toast.push({
          tone: r.readings > 0 ? "success" : "info",
          // Estate-wide, because that is what the endpoint does: the
          // same pass the scheduler runs on cadence. Saying "sampled"
          // would imply this row alone.
          message: t("modules.framework.kpi.sampled", {
            readings: r.readings,
            kpis: r.kpis,
          }),
          ...(r.readings === 0
            ? { detail: t("modules.framework.kpi.sampledNothing") }
            : {}),
        }),
      onError: () =>
        toast.push({ tone: "error", message: t("modules.framework.kpi.sampleFailed") }),
    });
  };

  return (
    <section data-testid="kpi-panel" className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h4 className="font-mono text-[11px] uppercase tracking-[0.12em] text-ink-muted">
          {t("modules.framework.kpi.heading")}
          {/* Round 12 H2 -- this explained what a FINDING is, beside a
              heading about effectiveness KPIs, to the persona whose
              whole job runs through metrics. Sixth round of a
              definition attached to the wrong thing. */}
          <InfoTip termKey="kpi" />
        </h4>
        <div className="flex items-center gap-2">
          {/* Round 11 D5 -- the verb, for the seat that can finish it.
              Sampling is `require_admin`, so a preparer gets the
              sentence on the row instead. Offering an action to a seat
              that cannot complete it is the repeat defect this
              programme spent four rounds closing. */}
          {isAdmin && waitingOnSampling && (
            <button
              type="button"
              onClick={runSample}
              disabled={sample.isPending}
              data-testid="kpi-sample-now"
              className="rounded border border-brand-brass/40 bg-brand-brass/5 px-2 py-1 text-xs text-brand-brassDark disabled:opacity-50"
            >
              {sample.isPending
                ? t("modules.framework.kpi.sampling")
                : t("modules.framework.kpi.sampleNow")}
            </button>
          )}
          <button
            type="button"
            onClick={runDerive}
            disabled={deriveToE.isPending || list.length === 0}
            data-testid="kpi-derive-toe"
            className="rounded border border-brand-brass/40 bg-brand-brass/5 px-2 py-1 text-xs text-brand-brassDark disabled:opacity-50"
          >
            {t("modules.framework.kpi.deriveToE")}
          </button>
        </div>
      </div>

      {list.length === 0 && (
        <p data-testid="kpi-empty" className="text-xs text-ink-2">
          {t("modules.framework.kpi.empty")}
        </p>
      )}

      {list.map((kpi) => (
        <KpiRow key={kpi.id} kpi={kpi} controlId={controlId} />
      ))}

      <AddKpiForm controlId={controlId} />
    </section>
  );
}

function KpiRow({
  kpi,
  controlId,
}: {
  kpi: ControlKpi;
  controlId: string;
}): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const addReading = useAddKpiReading(controlId);
  const deleteKpi = useDeleteKpi(controlId);
  const [value, setValue] = useState("");
  const [showTrend, setShowTrend] = useState(false);
  const readings = useKpiReadings(kpi.id, showTrend);
  const latest = kpi.latest_reading;
  // Readings arrive newest-first; oldest -> newest for the trend line.
  const series = (readings.data ?? []).map((r) => r.value).reverse();

  return (
    <div
      data-testid={`kpi-row-${kpi.id}`}
      className="rounded border border-paper-2 p-2 text-xs"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-ink">
          {kpi.name}
          {" · "}
          <span className="ms-1 font-mono text-[10px] uppercase text-ink-muted">
            {kpi.source}
          </span>
        </span>
        {latest ? (
          <StatusPill tone={latest.in_tolerance ? "green" : "red"}>
            {latest.value}
            {kpi.unit ? ` ${kpi.unit}` : ""}
          </StatusPill>
        ) : (
          <StatusPill tone="neutral">{t("modules.framework.kpi.noReading")}</StatusPill>
        )}
      </div>
      {/* Round 11 D5 -- why it says that, where she is reading it.
          An auto KPI a preparer adds herself reads NO READING for
          ever while sampling is off, and the sentence explaining it
          already existed -- under `latest &&`, so it rendered only
          when there was a reading, which is the one case it was not
          written for. The evidence list goes with it, and that list is
          what made this the best explanatory screen on her route. */}
      {!latest && kpi.source === "auto" && kpi.sampling_enabled === false && (
        <p
          data-testid={`kpi-why-no-reading-${kpi.id}`}
          className="mt-1 text-[11px] text-ink-muted"
        >
          {t("modules.framework.kpi.noReadingBecause")}
        </p>
      )}
      {/* C7 -- when this was measured, and what it counted. A control
          turned red on a number with no time against it and no route
          to the rows behind it, so the status could not be argued
          with in the surface where it appears. */}
      {latest && (
        <p
          data-testid={`kpi-provenance-${kpi.id}`}
          className="mt-1 text-[11px] text-ink-muted"
        >
          {t("modules.framework.kpi.measuredAt", {
            when: formatDateTime(latest.observed_at, i18n.language),
          })}
          {kpi.source === "auto" && ` \u00b7 ${t("modules.framework.kpi.estateWide")}`}
          {/* C3 -- when this number next moves. Saying nothing left a
              reader unable to tell a slow sample from a stopped one,
              and those want opposite responses. */}
          {kpi.source === "auto" &&
            (kpi.sampling_enabled
              ? kpi.next_sample_at &&
                ` \u00b7 ${t("modules.framework.kpi.nextSample", {
                  when: formatTime(kpi.next_sample_at, i18n.language),
                })}`
              : ` \u00b7 ${t("modules.framework.kpi.samplingOff")}`)}
        </p>
      )}
      {latest?.contributing_ids && latest.contributing_ids.length > 0 && (
        <p className="mt-0.5 flex flex-wrap items-center gap-1 text-[11px]">
          <span className="text-ink-muted">{t("modules.framework.kpi.counted")}</span>
          {/* C3 -- these read "#1 #2 #3": a link to something the
              reader cannot identify without opening it, on a number
              they are being asked to argue with. An id with no stored
              label falls back to the id, which is what was shown
              anyway. */}
          {latest.contributing_ids.slice(0, 12).map((id, i) => (
            <Link
              key={id}
              data-testid={`kpi-contributor-${kpi.id}`}
              to={
                latest.contributor_kind === "drill"
                  ? "/twin/drills"
                  : `/twin/findings/${id}`
              }
              className="text-brand-brassDark hover:underline"
            >
              {latest.contributor_labels?.[id] ?? `#${i + 1}`}
            </Link>
          ))}
          {latest.contributing_ids.length > 12 && (
            <span className="text-ink-muted">
              {t("modules.framework.kpi.andMore", {
                n: latest.contributing_ids.length - 12,
              })}
            </span>
          )}
        </p>
      )}
      <div className="mt-1 flex items-center gap-2 text-ink-2">
        <span>
          {t("modules.framework.kpi.target", {
            target: kpi.target,
            tolerance: kpi.tolerance,
          })}
        </span>
        {kpi.source === "manual" && (
          <>
            <input
              type="number"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={t("modules.framework.kpi.readingPlaceholder")}
              data-testid={`kpi-reading-input-${kpi.id}`}
              className="w-20 rounded border border-paper-2 bg-paper px-1 py-0.5"
            />
            <button
              type="button"
              onClick={() => {
                if (value.trim() !== "") {
                  addReading.mutate({ kpiId: kpi.id, value: Number(value) });
                  setValue("");
                }
              }}
              data-testid={`kpi-reading-save-${kpi.id}`}
              className="rounded bg-brand-brass px-2 py-0.5 text-brand-navy"
            >
              {t("modules.framework.kpi.record")}
            </button>
          </>
        )}
        <button
          type="button"
          onClick={() => setShowTrend((v) => !v)}
          data-testid={`kpi-trend-toggle-${kpi.id}`}
          className="ms-auto text-brand-brassDark hover:underline"
        >
          {t(
            showTrend
              ? "modules.framework.kpi.hideTrend"
              : "modules.framework.kpi.trend",
          )}
        </button>
        <button
          type="button"
          onClick={() =>
            deleteKpi.mutate(kpi.id, {
              // Round 9 E6 -- a row vanished from a list. Removing a
              // KPI changes what Test of Effectiveness will conclude
              // next time it is derived, which is not a fact the list
              // can show by one fewer row.
              onSuccess: () =>
                toast.push({
                  tone: "warning",
                  message: t("modules.framework.kpi.removed", { name: kpi.name }),
                }),
            })
          }
          data-testid={`kpi-delete-${kpi.id}`}
          className="text-rag-red hover:underline"
        >
          {t("modules.framework.kpi.remove")}
        </button>
      </div>
      {showTrend && (
        <div className="mt-1" data-testid={`kpi-trend-${kpi.id}`}>
          {series.length >= 2 ? (
            <Sparkline
              points={series}
              testid={`kpi-sparkline-${kpi.id}`}
              tint={latest?.in_tolerance ? "stroke-rag-green" : "stroke-brand-brass"}
            />
          ) : (
            <span className="text-[10px] text-ink-muted">
              {t("modules.framework.kpi.noTrend")}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function AddKpiForm({ controlId }: { controlId: string }): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const kpis = useControlKpis(controlId);
  const createKpi = useCreateKpi(controlId);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [source, setSource] = useState<"auto" | "manual">("auto");
  const [metricKey, setMetricKey] = useState("");
  const [target, setTarget] = useState("");
  const [tolerance, setTolerance] = useState("0");
  const [direction, setDirection] = useState("higher_is_better");

  const autoMetrics = kpis.data?.auto_metrics ?? [];

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="kpi-add-toggle"
        className="text-xs text-brand-brassDark hover:underline"
      >
        {t("modules.framework.kpi.addKpi")}
      </button>
    );
  }

  const submit = () => {
    if (target.trim() === "") return;
    const chosenMetric = metricKey || autoMetrics[0]?.key || null;
    const body: ControlKpiCreateIn =
      source === "auto"
        ? {
            name:
              name || autoMetrics.find((m) => m.key === chosenMetric)?.label || "KPI",
            source,
            metric_key: chosenMetric,
            target: Number(target),
            tolerance: Number(tolerance),
          }
        : {
            name,
            source,
            target: Number(target),
            tolerance: Number(tolerance),
            direction,
          };
    createKpi.mutate(body, {
      onSuccess: () => {
        setOpen(false);
        toast.push({
          tone: "success",
          message: t("modules.framework.kpi.added", { name: body.name }),
        });
      },
    });
  };

  return (
    <div
      data-testid="kpi-add-form"
      className="space-y-1 rounded border border-paper-2 p-2 text-xs"
    >
      {/* Round 11 D5 -- said BEFORE she commits to a metric that will
          not read. The row explains an empty reading after the fact;
          this is the moment she can still choose a manual KPI
          instead. Read from the list rather than from a row, so it is
          there on a control that has no KPIs yet. */}
      {source === "auto" && kpis.data?.sampling_enabled === false && (
        <p data-testid="kpi-add-sampling-off" className="text-[11px] text-ink-muted">
          {t("modules.framework.kpi.addAutoSamplingOff")}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <select
          value={source}
          onChange={(e) => setSource(e.target.value as "auto" | "manual")}
          data-testid="kpi-add-source"
          className="rounded border border-paper-2 bg-paper px-1 py-0.5"
        >
          <option value="auto">{t("modules.framework.kpi.sourceAuto")}</option>
          <option value="manual">{t("modules.framework.kpi.sourceManual")}</option>
        </select>
        {source === "auto" ? (
          <select
            value={metricKey}
            onChange={(e) => setMetricKey(e.target.value)}
            data-testid="kpi-add-metric"
            className="rounded border border-paper-2 bg-paper px-1 py-0.5"
          >
            <option value="">{t("modules.framework.kpi.pickMetric")}</option>
            {autoMetrics.map((m) => (
              <option key={m.key} value={m.key}>
                {m.label} ({m.unit})
              </option>
            ))}
          </select>
        ) : (
          <>
            <input
              dir="auto"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("modules.framework.kpi.namePlaceholder")}
              data-testid="kpi-add-name"
              className="rounded border border-paper-2 bg-paper px-1 py-0.5"
            />
            <select
              value={direction}
              onChange={(e) => setDirection(e.target.value)}
              data-testid="kpi-add-direction"
              className="rounded border border-paper-2 bg-paper px-1 py-0.5"
            >
              <option value="higher_is_better">
                {t("modules.framework.kpi.higherBetter")}
              </option>
              <option value="lower_is_better">
                {t("modules.framework.kpi.lowerBetter")}
              </option>
            </select>
          </>
        )}
        <input
          type="number"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          placeholder={t("modules.framework.kpi.targetPlaceholder")}
          data-testid="kpi-add-target"
          className="w-20 rounded border border-paper-2 bg-paper px-1 py-0.5"
        />
        <input
          type="number"
          value={tolerance}
          onChange={(e) => setTolerance(e.target.value)}
          placeholder={t("modules.framework.kpi.tolerancePlaceholder")}
          data-testid="kpi-add-tolerance"
          className="w-20 rounded border border-paper-2 bg-paper px-1 py-0.5"
        />
        <button
          type="button"
          onClick={submit}
          data-testid="kpi-add-submit"
          className="rounded bg-brand-brass px-2 py-0.5 text-brand-navy"
        >
          {t("modules.framework.kpi.add")}
        </button>
      </div>
    </div>
  );
}
